import React, { useEffect, useMemo, useState } from 'react';
import { ModalShell } from '../ds/ModalShell';
import { InputField } from '../ds/InputField';
import { Button } from '../ds/components/Button';
import { Icon } from '../ds/components/Icon';
import { apartmentInspectionsApi } from '../services/api';
import type { BulkAbsenceResult } from '../services/api';
import type { InspectionTerm } from '../types';
import {
  absenceDayError,
  absenceMinDay,
  bulkAbsenceSummary,
  earliestTermDay,
  formatDayPl,
  previewBulkAbsence,
  sortApartmentNumbers,
  todayLocalDay,
  type BulkAbsenceRecordLike,
} from '../utils/bulkAbsence';
import { lokaleLabel } from '../utils/plural';

export interface BulkAbsenceModalProps {
  /** Zaznaczone lokale; `null` = okno zamknięte. */
  apartments: { id: string; apartmentNumber: string }[] | null;
  inspectionId: string;
  /** Terminy przeglądu (`inspection.terms`) — dolna granica dnia nieobecności. */
  terms?: InspectionTerm[] | null;
  /** Adres budynku do tytułu. */
  subject?: string;
  onClose: () => void;
  /**
   * Stan lokali na serwerze mógł się zmienić — rodzic odświeża budynek PO zamknięciu okna (odświeżenie w trakcie
   * zdjęłoby podsumowanie). `saved` = zapisano co najmniej jeden lokal (wtedy rodzic czyści też zaznaczenie).
   * Także po błędzie zapisu i po „już w historii": odpowiedź mogła zginąć po udanym zapisie (review PR #420).
   */
  onDone: (r: { saved: boolean }) => void;
}

const banner = (kind: 'error' | 'ok'): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 'var(--space-2)',
  padding: 'var(--space-2) var(--space-3)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--text-label)',
  background: kind === 'error' ? 'var(--status-deny-bg)' : 'var(--status-ok-bg)',
  border: `var(--border-width) solid ${kind === 'error' ? 'var(--status-deny-bar)' : 'var(--status-ok-bar)'}`,
  color: kind === 'error' ? 'var(--status-deny)' : 'var(--status-ok)',
});

const td: React.CSSProperties = {
  padding: 'var(--space-2) var(--space-3)',
  borderBottom: 'var(--border-width) solid var(--border)',
  color: 'var(--text-primary)',
  verticalAlign: 'top',
};

/**
 * „Oznacz nieobecność" dla zaznaczonych lokali (Bronek 2026-10-14: „Czasem gdy tablet zawiedzie chłopaki piszą
 * na kartkach"). Dzień jak na tablecie (domyślnie dziś, nie z przyszłości, nie sprzed I terminu); lokale z wynikiem
 * kontroli są pomijane z powodem; podsumowanie po zapisie. Zapis: POST /apartment-inspections/bulk-absence —
 * serwer sprawdza wszystko jeszcze raz na stanie z chwili zapisu (podgląd tutaj to tylko informacja).
 */
export default function BulkAbsenceModal({ apartments, inspectionId, terms, subject, onClose, onDone }: BulkAbsenceModalProps) {
  const today = todayLocalDay();
  const firstTerm = earliestTermDay(terms);
  const [day, setDay] = useState(today);
  const [records, setRecords] = useState<BulkAbsenceRecordLike[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [result, setResult] = useState<BulkAbsenceResult | null>(null);

  const open = !!apartments && apartments.length > 0;
  // Klucz zamiast tablicy: rodzic podaje nowy literał co render, a odczyt robimy tylko przy zmianie wyboru.
  const key = open ? apartments!.map((a) => a.id).join(',') : '';

  useEffect(() => {
    setDay(today);
    setResult(null);
    setSaveError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Wersje rekordów (N1) i podgląd pominięć — świeży odczyt przy każdym otwarciu.
  useEffect(() => {
    if (!key) { setRecords(null); setLoadError(null); return undefined; }
    let stale = false; // guard anty-stale: zamknięcie w trakcie nie nadpisze stanu
    setRecords(null);
    setLoadError(null);
    apartmentInspectionsApi.getByInspectionId(inspectionId)
      .then((rows) => { if (!stale) setRecords(Array.isArray(rows) ? rows : []); })
      .catch((e) => {
        console.error('Odczyt rekordów lokali przed zbiorczą nieobecnością nieudany:', e);
        if (!stale) setLoadError('Nie udało się wczytać stanu lokali z serwera. Sprawdź połączenie i spróbuj ponownie.');
      });
    return () => { stale = true; };
  }, [key, inspectionId, reloadKey]);

  const dayError = absenceDayError(day, today, firstTerm);
  const preview = useMemo(
    () => (open && records ? sortApartmentNumbers(previewBulkAbsence(apartments!, records, day)) : []),
    [open, apartments, records, day],
  );
  const toMark = preview.filter((p) => !p.skipReason);

  const submit = async () => {
    if (!open || !records || dayError || toMark.length === 0) return;
    setSaving(true);
    setSaveError(null);
    try {
      // Wysyłamy WSZYSTKIE zaznaczone — serwer pomija z powodem także te, które podgląd już oznaczył.
      const res = await apartmentInspectionsApi.bulkAbsence({
        inspectionId,
        date: day,
        apartments: preview.map((p) => ({ apartmentId: p.apartmentId, expectedUpdatedAt: p.expectedUpdatedAt })),
      });
      setResult(res);
      if (res.marked.length > 0 || res.skipped.some((x) => x.code === 'ALREADY_RECORDED')) {
        onDone({ saved: res.marked.length > 0 });
      }
    } catch (e) {
      console.error('Zbiorcza nieobecność nieudana:', e);
      // Odpowiedź mogła zginąć po zapisie na serwerze — po zamknięciu okna lista ma pokazać stan z serwera.
      onDone({ saved: false });
      setSaveError(e instanceof Error && e.message ? e.message : 'Nie udało się oznaczyć nieobecności. Sprawdź połączenie z serwerem.');
    } finally {
      setSaving(false);
    }
  };

  const footer = result ? (
    <Button variant="primary" onClick={onClose}>Zamknij</Button>
  ) : (
    <>
      <Button variant="secondary" onClick={onClose} disabled={saving}>Anuluj</Button>
      <Button
        variant="primary"
        icon="check"
        onClick={submit}
        disabled={saving || !records || !!dayError || toMark.length === 0}
      >
        {saving ? 'Zapisywanie…' : `Zapisz nieobecność (${toMark.length})`}
      </Button>
    </>
  );

  return (
    <ModalShell
      open={open}
      title={subject ? `Oznacz nieobecność — ${subject}` : 'Oznacz nieobecność'}
      width={640}
      onClose={onClose}
      footer={footer}
    >
      <div data-testid="bulk-absence" style={{ display: 'grid', gap: 'var(--space-4)', fontFamily: 'var(--font-sans)' }}>
        {!result && (
          <>
            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 'var(--text-label)' }}>
              Nieobecność z kartki (np. gdy tablet zawiódł) dla {lokaleLabel(apartments?.length || 0)} naraz. Lokale z wynikiem
              kontroli zostaną pominięte. W budynku ze wspólną siecią lub kotłem C.O. lokal bez danych o źródłach zostanie
              oznaczony jako podłączony do wspólnego źródła — jak na tablecie.
            </p>
            <InputField
              label="Dzień nieobecności"
              type="date"
              value={day}
              min={absenceMinDay(today, firstTerm)}
              max={today}
              onChange={(e) => { setDay(e.target.value); setSaveError(null); }}
              errorMessage={dayError || undefined}
              hint={!dayError && day < today ? `Data wsteczna: ${formatDayPl(day)}` : undefined}
              containerStyle={{ maxWidth: 260 }}
            />
            {loadError && (
              <div role="alert" style={banner('error')}>
                <Icon name="triangle-alert" size={18} />
                <span style={{ flex: 1 }}>{loadError}</span>
                <Button variant="secondary" onClick={() => setReloadKey((k) => k + 1)}>Spróbuj ponownie</Button>
              </div>
            )}
            {!records && !loadError && <div style={{ color: 'var(--text-secondary)' }}>Ładowanie stanu lokali…</div>}
            {saveError && (
              <div role="alert" style={banner('error')}>
                <Icon name="triangle-alert" size={18} />
                <span>{saveError}</span>
              </div>
            )}
            {records && (
              <div style={{ overflow: 'auto', maxHeight: '45vh' }}>
                <table aria-label="Zaznaczone lokale" style={{ borderCollapse: 'collapse', width: '100%', fontSize: 'var(--text-label)' }}>
                  <tbody>
                    {preview.map((p) => (
                      <tr key={p.apartmentId}>
                        <td style={{ ...td, fontWeight: 'var(--weight-semibold)' as any, whiteSpace: 'nowrap' }}>Lokal {p.apartmentNumber}</td>
                        <td style={{ ...td, color: p.skipReason ? 'var(--status-rec)' : 'var(--text-secondary)' }}>
                          {p.skipReason ? `Zostanie pominięty: ${p.skipReason}` : 'Zostanie oznaczony'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {result && (
          <>
            <div role="status" style={banner(result.marked.length > 0 ? 'ok' : 'error')}>
              <Icon name={result.marked.length > 0 ? 'check' : 'triangle-alert'} size={18} />
              <span>{bulkAbsenceSummary(result)} Dzień: {formatDayPl(result.date)}.</span>
            </div>
            {result.marked.length > 0 && (
              <section aria-label="Oznaczone">
                <h3 style={{ margin: '0 0 var(--space-2)', fontSize: 'var(--text-body)', color: 'var(--text-heading)' }}>Oznaczone</h3>
                <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 'var(--text-label)' }}>
                  <tbody>
                    {sortApartmentNumbers(result.marked).map((m) => (
                      <tr key={m.apartmentId}>
                        <td style={{ ...td, fontWeight: 'var(--weight-semibold)' as any, whiteSpace: 'nowrap' }}>Lokal {m.apartmentNumber}</td>
                        <td style={{ ...td, color: 'var(--text-secondary)' }}>
                          {m.connectedDefault ? 'podłączony do wspólnego źródła ciepła (domyślnie)' : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            )}
            {result.skipped.length > 0 && (
              <section aria-label="Pominięte">
                <h3 style={{ margin: '0 0 var(--space-2)', fontSize: 'var(--text-body)', color: 'var(--text-heading)' }}>Pominięte</h3>
                <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 'var(--text-label)' }}>
                  <tbody>
                    {sortApartmentNumbers(result.skipped).map((s) => (
                      <tr key={s.apartmentId}>
                        <td style={{ ...td, fontWeight: 'var(--weight-semibold)' as any, whiteSpace: 'nowrap' }}>
                          {s.apartmentNumber ? `Lokal ${s.apartmentNumber}` : 'Lokal'}
                        </td>
                        <td style={{ ...td, color: 'var(--status-rec)' }}>{s.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            )}
          </>
        )}
      </div>
    </ModalShell>
  );
}
